import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  getFieldValuesByTableAndId,
  getOtherRefSearchDropdown,
  getOtherRefSearchDropdownCount,
} from "../../api/keyvalue";
import { Icon, Label, Search } from "semantic-ui-react";
import { X } from "react-feather";
import useAuthority from "../../hooks/useAuthority";
import useFieldVisibility from "../../hooks/useFieldVisibility";
import { debounce } from "../../helper";

const resultRenderer = ({ title }) => (
  <Label
    content={title}
    style={{
      display: "block",
      width: "100%",
    }}
  />
);

const SearchDropdown = forwardRef(
  (
    {
      key,
      editMode,
      onResultSelect,
      reset,
      setReset = () => {},
      api,
      apiDependency = {},
      fieldInfo = { tableName: "", primaryKey: "", fieldName: "", id: "" },
      defaultValue = "",
      value = null,
      threshold = 2,
      multiGrid = false,
      multiGridCurrentValue,
      occupySpace = true,
      disabled = false,
      readOnly = false,
      multiple = false,
      multiValueTwoWayBinding = false,
      multiValueRemoveHandler = () => {},
      multiValues = {},
      clearable = false,
      onClear = () => {},
      returnFullObject = false,
      required = false,
      label,
      error,
      resetError = () => {},
      module = "",
      form = "",
      field = "",
      authenticated = false,
      style,
      hideMultiValueList = false,
      useDynamicFields = false,
      checkCount = false,
      reqCount = 30,
      singleTwoWayBinding = false,
    },
    ref,
  ) => {
    const [dataList, setDataList] = useState([]);
    const [fullDataList, setFullDataList] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [currentValue, setCurrentValue] = useState(defaultValue);
    const [currentMultiValues, setCurrentMultiValues] = useState({});
    const [isValueSet, setIsValueSet] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [treatAsSelect, setTreatAsSelect] = useState(false);
    const lastSearchTextRef = useRef("");

    const {
      visible,
      mandatory,
      label: currentLabel,
    } = useFieldVisibility({ field, useDynamicFields, form });

    const { view, authorized } = useAuthority({
      module,
      form,
      field,
      editMode,
      authenticated,
    });

    const stableApiDepJson = useMemo(() => {
      const sortKeys = (v) =>
        v && typeof v === "object" && !Array.isArray(v)
          ? Object.keys(v)
              .sort()
              .reduce((acc, k) => {
                acc[k] = sortKeys(v[k]);
                return acc;
              }, {})
          : v;
      return JSON.stringify(sortKeys(apiDependency));
    }, [apiDependency]);

    const requestSeqRef = useRef(0);

    useEffect(() => {
      (async () => {
        if (checkCount) {
          setSearchLoading(true);
          const requestData = JSON.parse(stableApiDepJson);
          const count = await getOtherRefSearchDropdownCount({
            ...requestData,
            reqCount: reqCount ?? 2,
          });
          if (api === getOtherRefSearchDropdown) {
            if (count !== 0 && count < reqCount) {
              const response = await getOtherRefSearchDropdown({
                ...requestData,
                searchText: "",
              });

              if (count === 1) {
                const data =
                  response !== null && response?.length === 1
                    ? response[0]
                    : null;

                if (data !== null) {
                  onResultSelect(returnFullObject ? data : data?.value);
                  setCurrentValue(data?.title);
                  setDataList([data]);
                  setFullDataList([data]);
                  setTreatAsSelect(true);
                }
              } else {
                const data =
                  response !== null && response?.length > 1 ? response : [];

                if (data.length > 1) {
                  setDataList(data);
                  setFullDataList(data);
                  setTreatAsSelect(true);
                }
              }
            }
          }
          setSearchLoading(false);
        }
      })();
    }, [stableApiDepJson, checkCount]);

    useEffect(() => {
      if (multiValueTwoWayBinding) {
        setCurrentMultiValues((prevMultiValues) => {
          if (JSON.stringify(prevMultiValues) !== JSON.stringify(multiValues)) {
            return multiValues;
          }
          return prevMultiValues;
        });
      }
    }, [multiValues, multiValueTwoWayBinding]);

    useEffect(() => {
      if (currentValue) {
        setIsValueSet(true);
      } else {
        setIsValueSet(false);
      }
    }, [currentValue]);

    const fieldInfoRef = useRef();
    fieldInfoRef.current = fieldInfo;

      useImperativeHandle(
          ref,
          () => ({
              reset: () => {
                  setDataList([]);
                  setSearchLoading(false);
                  setIsValueSet(false);
                  setCurrentValue(defaultValue);
                  lastSearchTextRef.current = "";
              },
              inputValue: currentValue,
              refreshResults: async () => {
                  const text = lastSearchTextRef.current;
                  if (!text || text.length < threshold) return;

                  setSearchLoading(true);
                  try {
                      const dep = JSON.parse(stableApiDepJson || "{}");
                      let requestData = { searchText: text };
                      if (Object.keys(dep).length > 0) {
                          requestData = { ...requestData, ...dep };
                      }

                      let response = [];
                      if (Object.keys(dep).length > 0) {
                          response = await api(requestData);
                      } else {
                          response = await api(text);
                      }

                      setDataList(response ?? []);
                      setFullDataList(response ?? []);
                  } catch (e) {
                      console.error(e);
                  } finally {
                      setSearchLoading(false);
                  }
              },
          }),
          [currentValue, defaultValue, api, stableApiDepJson, threshold],
      );

    useEffect(() => {
      if (multiGrid) {
        setCurrentValue(multiGridCurrentValue);
      }
    }, [multiGridCurrentValue, multiGrid]);

    useEffect(() => {
      (async () => {
        if (editMode) {
          setSearchLoading(true);
          if (fieldInfoRef.current.id) {
            const response = await getFieldValuesByTableAndId({
              tableName: fieldInfoRef.current.tableName,
              primaryKey: fieldInfoRef.current.primaryKey,
              fieldNames: fieldInfoRef.current.fieldName,
              ids: fieldInfoRef.current.id.toString(),
            });
            if (response && response.length > 0) {
              setCurrentValue(response[0].field1);
            }
          }
          setSearchLoading(false);
        }
      })();
    }, [editMode, value]);

    useEffect(() => {
      if (reset) {
        setDataList([]);
        setSearchLoading(false);
        setCurrentValue(defaultValue);
        if (multiple) {
          setCurrentMultiValues({});
        }
      }
      setReset(false);
    }, [reset, defaultValue, setReset, multiple]);

    // Debounced SERVER search
    const debouncedFilterDataFromServer = useMemo(
      () =>
        debounce(async (data) => {
          const text = data?.value ?? "";
          if (text.length < threshold) {
            setShowResults(false);
            setDataList([]);
            setSearchLoading(false);
            return;
          }

          setShowResults(true);
          setSearchLoading(true);

          // build request payload (same as your current logic)
          let requestData = { searchText: text };
          const dep = JSON.parse(stableApiDepJson || "{}");
          if (Object.keys(dep).length > 0) {
            for (const [k, v] of Object.entries(dep)) requestData[k] = v;
          }

          // race-proof: only latest response may update state
          const seq = ++requestSeqRef.current;
          try {
            let response = [];
            if (Object.keys(dep).length > 0) {
              response = await api(requestData); // api({...})
            } else {
              response = await api(requestData.searchText); // api(searchText)
            }

            if (seq !== requestSeqRef.current) return; // stale → drop
            setDataList(response);
          } catch (e) {
            console.error && console.error(e);
          } finally {
            if (seq === requestSeqRef.current) setSearchLoading(false);
          }
        }, 400),
      [api, threshold, stableApiDepJson],
    );

    const filterDataLocally = (data) => {
      const text = (data?.value ?? "").trim().toLowerCase();

      if (!text) {
        setDataList(fullDataList);
        return;
      }

      setDataList(
        fullDataList.filter((item) =>
          (item.title ?? "").toLowerCase().includes(text),
        ),
      );
    };

    const handleSearchChange = async (e, data) => {
      setCurrentValue(data.value);
      if (isValueSet) {
        setIsValueSet(false);
        onClear();
      } else if (!data?.value || data?.value?.trim() === "") {
        onClear();
      }
      if (treatAsSelect) {
        if (data.value !== null) {
          filterDataLocally(data);
        }
      } else {
        debouncedFilterDataFromServer(data);
      }
    };

    const clearClickHandler = () => {
      if (disabled || readOnly) {
        return;
      }
      setCurrentValue("");
      setIsValueSet(false);
      onClear();
    };

    const icons = clearable
      ? {
          icon: isValueSet ? (
            <Icon name="x" link onClick={clearClickHandler} />
          ) : (
            <Icon name="search" />
          ),
        }
      : {
          icon: <Icon name="search" />,
        };

    if (useDynamicFields && !visible) {
      return;
    }
    return (
      <>
        <style>
          {`
            .search-dropdown { width: 100%; }
            .search-dropdown > .ui.input { width: 100%; }        /* make input full width */
            .search-dropdown .results {
                width: 100% !important;       /* match input width */
                min-width: 100% !important;
                max-width: none !important;   /* remove 300px cap */
                left: 0 !important;           /* align with input’s left edge */
                max-height: 160px;
                overflow-y: auto;
            }
          `}
        </style>
        <div style={style ?? {}} className={`field ${error ? "error" : ""}`}>
          <label
            style={
              view
                ? useDynamicFields
                  ? visible
                    ? {}
                    : { display: "none" }
                  : {}
                : { display: "none" }
            }
          >
            {((useDynamicFields && mandatory) ||
              (!useDynamicFields && required)) && (
              <label
                style={{
                  color: "#9f3a38",
                  fontSize: "20px",
                  marginRight: "2px",
                }}
              >
                *
              </label>
            )}
            {useDynamicFields ? currentLabel : label}
          </label>
          <Search
            key={key}
            loading={searchLoading}
            resultRenderer={resultRenderer}
            input={icons}
            onFocus={() => {
              if (dataList.length > 0) {
                setShowResults(true);
              }
            }}
            onBlur={() => setShowResults(false)}
            style={
              view
                ? useDynamicFields
                  ? visible
                    ? {}
                    : { display: "none" }
                  : {}
                : { display: "none" }
            }
            onClick={() => {
              if (error) {
                resetError();
              }
            }}
            onResultSelect={(e, data) => {
              setShowResults(false);
              if (multiple) {
                if (!multiValueTwoWayBinding) {
                  setCurrentMultiValues((prev) => ({
                    ...prev,
                    [data.result.value]: data.result.title,
                  }));
                }
                // the input is only a search box here — the picked item is
                // shown in the value list below, so drop the typed text
                setCurrentValue("");
                setIsValueSet(false);
              } else {
                setIsValueSet(true);
                if (!singleTwoWayBinding) {
                  setCurrentValue(data.result.title);
                }
              }

              if (!treatAsSelect) {
                setDataList([]);
              } else if (multiple) {
                // input is empty again, so the local list must be unfiltered
                setDataList(fullDataList);
              }

              onResultSelect(
                returnFullObject ? data.result : data.result.value,
              );
            }}
            onSearchChange={handleSearchChange}
            results={dataList}
            open={showResults}
            value={currentValue}
            disabled={disabled || readOnly || !authorized}
            className="search-dropdown"
          />
          <small style={{ color: "#9f3a38" }}>{`${error ? error : ""}`}</small>
          {multiple &&
            !hideMultiValueList &&
            (Object.entries(currentMultiValues).length > 0 || occupySpace) && (
              <div
                style={{
                  display: "flex",
                  gap: "2px",
                  width: "100%",
                  minHeight: "50px",
                  marginTop: "10px",
                  flexWrap: "wrap",
                }}
              >
                {Object.entries(currentMultiValues).map(([key, value]) => (
                  <div
                    key={key}
                    style={{
                      padding: "10px",
                      backgroundColor: "#e8e8e8",
                      height: "40px",
                      borderRadius: "5px",
                      display: "flex",
                      gap: "2px",
                      alignItems: "center",
                      width: "fit-content",
                    }}
                  >
                    {value}
                    <X
                      size={"16px"}
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        if (disabled) return;
                        setCurrentMultiValues((prev) => {
                          const { [key]: _, ...rest } = prev;
                          return rest;
                        });
                        multiValueRemoveHandler(key);
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
        </div>
      </>
    );
  },
);

export default SearchDropdown;
